import SosIcon from '@mui/icons-material/Sos';
import { IconButton, Tooltip } from '@mui/material';
import { DateTime } from 'luxon';
import React, { type FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useCreateEvent, useEndEvent } from '../api/index.js';
import { useCheckPermissions } from '../auth/index.js';
import { type Event, EventCategory, EventPriority } from '../models/index.js';
import { toggleEmergencyInProgress } from '../redux/reducers/common.js';
import { isEmergencyInProgressSelector } from '../redux/selectors.js';
import { useEvents } from '../utils/index.js';


interface EmergencyButtonProps {
  patientId: string;
}

const EmergencyButton: FC<EmergencyButtonProps> = ({ patientId }) => {
  const { canCreateEvent } = useCheckPermissions();

  const emergencyInProgress = useSelector(isEmergencyInProgressSelector(patientId));
  const createEvent = useCreateEvent();
  const endEvent = useEndEvent();
  const dispatch = useDispatch();
  const { activeEvents, refetch } = useEvents(patientId ? [patientId] : []);

  useEffect(() => {
    const activeEmergency = activeEvents.find((event) => {
      return event.category === EventCategory.EMERGENCY;
    });

    dispatch(toggleEmergencyInProgress({ patientId, event: activeEmergency }));
  }, [activeEvents]);

  const startEmergency = async () => {
    if (window.confirm('Are you sure you want to start an emergency?')) {
      await createEvent.mutateAsync({
        patientId: patientId!,
        priority: EventPriority.HIGH,
        category: EventCategory.EMERGENCY,
        start: DateTime.now().toUTC().toISO(),
      });
      refetch();
    }
  };

  const endEmergency = async (event: Event) => {
    if (window.confirm('Are you sure you want to end the emergency?')) {
      await endEvent.mutateAsync(event.id);
      refetch();
    }
  };

  if (!canCreateEvent) {
    return null;
  }
  return (
    <Tooltip
      arrow
      color="primary"
      placement="bottom"
      title={`${emergencyInProgress ? 'End' : 'Start'} Emergency`}
    >
      <IconButton
        color="inherit"
        aria-label={`${emergencyInProgress ? 'End' : 'Start'} Emergency`}
        edge="start"
        sx={{
          ...(!emergencyInProgress && { backgroundColor: 'red !important' }),
        }}
        onClick={emergencyInProgress ? () => endEmergency(emergencyInProgress) : startEmergency}
      >
        <SosIcon />
      </IconButton>
    </Tooltip>
  );
};

export default EmergencyButton;
